'use client';

import React, { useState, useEffect, useCallback, useRef, Suspense, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { message, Spin, Input } from 'antd';
import { 
  EnvironmentOutlined,
  CompassOutlined,
  CarOutlined,
  DollarOutlined,
  StarFilled,
  SearchOutlined,
  AimOutlined,
  DownOutlined
} from '@ant-design/icons';
import { Entity } from './types';
import { TripStop } from './types/trip';
import type { AssistantMessage, AssistantRole } from './types/assistant';
import TripPlannerLadder from './components/TripPlannerLadder';
import { buildTripLegs } from './utils/tripPlanner';
import { loadSavedTrips, upsertTrip } from './utils/tripStorage';
import type { StoredTrip } from './utils/tripStorage';
import { search, type EnhancedSearchParams, getEnhancedLocationSuggestions } from './lib/searchEntities';
import HeroSearch from './components/HeroSearch';
import EntityCard from './components/EntityCard';
import GoogleMapComponent from './components/GoogleMapComponent';
import {
  createAgentSession,
  sendAgentMessage,
  summariseTripForAssistant,
  inferAssistantTaskType,
} from './utils/agentAssistant';

const uniqueId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `trip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const computeTripRouteKey = (stops: TripStop[]): string =>
  stops
    .map((stop) => stop.name?.trim().toLowerCase())
    .filter(Boolean)
    .join('|');

interface ParsedRouteQuery {
  start: string;
  end: string;
  waypoints: string[];
}

const parseRouteQuery = (raw: string): ParsedRouteQuery | null => {
  if (!raw) return null;
  const [first, ...restParts] = raw.split(/\s+to\s+/i);
  if (!first || restParts.length === 0) return null;
  const start = first.trim();
  const remainder = restParts.join(' to ').trim();
  if (!start || !remainder) return null;

  const viaSegments = remainder.split(/\s+via\s+/i);
  const end = viaSegments[0]?.trim();
  if (!end) return null;

  const waypoints = viaSegments
    .slice(1)
    .flatMap((segment) =>
      segment
        .split(/,|;|\band\b/i)
        .map((token) => token.trim())
        .filter(Boolean)
    );

  return { start, end, waypoints };
};

type GeocodeCoordinate = {
  lat: number;
  lng: number;
  boundingBox?: [number, number, number, number];
  type?: string;
  class?: string;
};

const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';

const haversineDistanceKm = (a: GeocodeCoordinate, b: GeocodeCoordinate): number => {
  const R = 6371; // Earth radius in km
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    sinLon * sinLon * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
};

const fetchCoordinatesForPlace = async (place: string): Promise<GeocodeCoordinate | null> => {
  const trimmed = place.trim();
  if (!trimmed) {
    return null;
  }

  const url = `${NOMINATIM_ENDPOINT}?format=json&q=${encodeURIComponent(
    `${trimmed}, Australia`
  )}&countrycodes=au&limit=1`;

  const response = await fetch(url, {
    headers: {
      'User-Agent': 'CampsiteExplorer/1.0',
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Geocode request failed: ${response.status}`);
  }

  const data = await response.json();
  const entry = Array.isArray(data) && data.length > 0 ? data[0] : null;
  if (!entry) {
    return null;
  }

  const lat = parseFloat(entry.lat);
  const lng = parseFloat(entry.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const boundingBox =
    Array.isArray(entry.boundingbox) && entry.boundingbox.length === 4
      ? (entry.boundingbox.map((value: string) => parseFloat(value)) as [
          number,
          number,
          number,
          number
        ])
      : undefined;

  return {
    lat,
    lng,
    boundingBox,
    type: entry.type,
    class: entry.class,
  };
};

const createTripStopFromName = (
  name: string,
  kind: TripStop['kind'],
  options: Partial<TripStop> = {}
): TripStop => ({
  id: options.id ?? uniqueId(),
  name,
  kind,
  locked: options.locked ?? false,
  entityId: options.entityId,
  lat: options.lat,
  lng: options.lng,
  tags: options.tags ?? [],
  constraints: options.constraints ?? [],
  notes: options.notes,
});

const toFiniteNumber = (value?: string | number): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const createTripStopFromEntity = (entity: Entity): TripStop => {
  const address = entity.addresses?.[0];
  const lat =
    toFiniteNumber(address?.gps_latitude) ?? toFiniteNumber(address?.waypoint_latitude);
  const lng =
    toFiniteNumber(address?.gps_longitude) ??
    toFiniteNumber(address?.waypoint_longitude);

  const tags =
    entity.features
      ?.filter((feature) => feature.desc)
      .map((feature) => feature.desc!.toLowerCase())
      .slice(0, 6) ?? [];

  return {
    id: `stop-${entity.id}-${uniqueId()}`,
    name: entity.name ?? 'Campsite',
    kind: 'camp',
    entityId: entity.id,
    lat,
    lng,
    tags,
    constraints: [],
  };
};

const mergeTripStopIntoList = (
  stops: TripStop[],
  stop: TripStop
): { added: boolean; next: TripStop[] } => {
  const alreadyExists = stops.some((existing) => {
    if (existing.entityId && stop.entityId) {
      return existing.entityId === stop.entityId;
    }
    return existing.name.trim().toLowerCase() === stop.name.trim().toLowerCase();
  });

  if (alreadyExists) {
    return { added: false, next: stops };
  }

  if (stops.length < 2) {
    return { added: true, next: [...stops, stop] };
  }

  const withoutTail = stops.slice(0, -1);
  const tail = stops.slice(-1);
  return { added: true, next: [...withoutTail, stop, ...tail] };
};

const convertTripStopsToEntities = (stops: TripStop[]): Entity[] =>
  stops
    .map((stop, index) => {
      const hasCoords =
        typeof stop.lat === 'number' && typeof stop.lng === 'number';

      if (!hasCoords) return null;

      return {
        id: stop.entityId ?? -(index + 1),
        name: stop.name,
        short_desc: stop.kind,
        desc: undefined,
        image_src: undefined,
        display_src: undefined,
        gallery_images: [],
        rating: undefined,
        addresses: [
          {
            gps_latitude: String(stop.lat),
            gps_longitude: String(stop.lng),
            town: undefined,
            state: undefined,
          },
        ],
        features:
          stop.tags?.map((tag, idx) => ({
            id: idx,
            desc: tag,
            feature_type: 'TEXT',
          })) ?? [],
      } as unknown as Entity;
    })
    .filter((entity): entity is Entity => Boolean(entity));

function HomePageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParamsKey = searchParams?.toString();
  
  // STATE
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('All');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [radius, setRadius] = useState<number>(50);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('list');
  const [options, setOptions] = useState<{ value: string }[]>([]);
  
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: -27.5,  // Shifted south to include Tasmania
    lng: 133.7751
  });
  const [mapZoom, setMapZoom] = useState<number>(3.9);  // Slightly wider overview zoom
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState<boolean>(true);
  
  // Route search state
  const [isRouteSearch, setIsRouteSearch] = useState<boolean>(false);
  const [startPoint, setStartPoint] = useState<string>('');
  const [endPoint, setEndPoint] = useState<string>('');
  const [routeName, setRouteName] = useState<string>('');
  const [routeCorridorWidth, setRouteCorridorWidth] = useState<number>(60);
  const [tripStops, setTripStops] = useState<TripStop[]>([]);
  const [tripRouteKey, setTripRouteKey] = useState<string | null>(null);
  const [mapViewMode, setMapViewMode] = useState<'search' | 'trip'>('search');
  const [routeSummary, setRouteSummary] = useState<{ distanceKm?: number; driveMinutes?: number }>({});
  const [activeTripId, setActiveTripId] = useState<string | null>(null);
  const [savedTrips, setSavedTrips] = useState<StoredTrip[]>([]);
  const [isEditingTripName, setIsEditingTripName] = useState(false);
  const [tripNameInput, setTripNameInput] = useState('');
  const [tripStatusMessage, setTripStatusMessage] = useState<{
    message: string;
    tone: 'success' | 'warning' | 'error';
  } | null>(null);
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => () => setMapReady(false), []);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantHasUnread, setAssistantHasUnread] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>(() => [
    {
      id: uniqueId(),
      role: 'assistant',
      content:
        'Hi there! I can tweak your trip planner. Ask me to add or remove stops, adjust the search radius, or widen the corridor and I will update the ladder for you.',
      createdAt: Date.now(),
    },
  ]);
  const [assistantIsProcessing, setAssistantIsProcessing] = useState(false);
  const [assistantBooting, setAssistantBooting] = useState(true);
  const [agentSessionId, setAgentSessionId] = useState<string | null>(null);
  const [agentHandoff, setAgentHandoff] = useState<string | null>(null);
  const [, setAgentContext] = useState<Record<string, string>>({});
  const [isTripSectionOpen, setIsTripSectionOpen] = useState(false);
  const [visibleCardCount, setVisibleCardCount] = useState(48);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    if (!isRouteSearch || tripStops.length < 2) {
      setRouteSummary({});
    }
  }, [isRouteSearch, tripStops.length]);
  
  // REFS
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const entityRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevTripStopCountRef = useRef(tripStops.length);
  const geocodeCacheRef = useRef<Map<string, GeocodeCoordinate>>(new Map());
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Scroll tracking for back-to-top button
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const onScroll = () => {
      setShowBackToTop(sidebar.scrollTop > 600);
    };
    sidebar.addEventListener('scroll', onScroll, { passive: true });
    return () => sidebar.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    sidebarRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // CONSTANTS
  const features = [
    // Essentials & Amenities
    'Powered Sites',
    'Unpowered Sites',
    'Toilets',
    'Hot Showers',
    'Showers',
    'Drinking Water',
    'Camp Kitchen',
    'Laundry',
    'Dump Point',
    'Accessible Facilities',
    'Campfires Allowed',
    // Comfort & Services
    'Cabins',
    'Glamping',
    'WiFi',
    'BBQ',
    'Playground',
    'Swimming Pool',
    'Family Friendly',
    'Pet Friendly',
    'On-site Cafe',
    'Firewood Available',
    // Outdoor & Adventure
    'River Access',
    'Beach Access',
    'Lake Access',
    'Boat Ramp',
    'Fishing',
    'Hiking Trails',
    'Cycling Trails',
    '4WD Access',
    'Horse Riding',
    'Stargazing',
    // Points of Interest
    'Lookout',
    'Waterfall',
    'Scenic Drive',
    'National Park Visitor Centre',
    'Wildlife Park',
    'Botanic Gardens',
    'Historic Site',
    'Heritage Walk',
    'Museum',
    'Art Gallery',
    'Winery',
    'Brewery',
    'Distillery',
    'Farm Gate',
    'Local Market',
  ];

  const toggleAssistant = useCallback(() => {
    setAssistantOpen((prev) => {
      const next = !prev;
      if (next) {
        setAssistantHasUnread(false);
      }
      return next;
    });
  }, []);

  const assistantSuggestions = useMemo(() => {
    if (isRouteSearch) {
      return [
        'Add a pet-friendly campsite near Bright',
        'Make the corridor width 80 km',
        'Remove the last stop from the trip',
      ];
    }
    return [
      'Plan a trip from Melbourne to Adelaide',
      'Set the search radius to 120 km',
      'Find free camping with toilets near Byron Bay',
    ];
  }, [isRouteSearch]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      try {
        const session = await createAgentSession({
          itinerary_summary: 'No trip stops yet.',
        });
        if (!cancelled) {
          setAgentSessionId(session.session_id);
          setAgentContext(session.context || {});
        }
      } catch (error) {
        console.error('Failed to initialise agent session:', error);
        if (!cancelled) {
          message.warning('Trip assistant is waking up, falling back to local actions for now.');
        }
      } finally {
        if (!cancelled) {
          setAssistantBooting(false);
        }
      }
    };

    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load featured campsites on mount
  useEffect(() => {
    const fetchFeaturedCampsites = async () => {
      try {
        const response = await search({
          location: 'Australia',
          radius: 500,
          per_page: 10,
          search_type: 'location'
        });
        setEntities(response.entities || []);
      } catch (error) {
        console.error('Failed to fetch featured campsites:', error);
      } finally {
        setIsLoadingFeatured(false);
      }
    };

    if (!isRouteSearch) {
      fetchFeaturedCampsites();
    }
  }, [isRouteSearch]);

  // Check for route parameters on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ location: string; radius?: number }>;
      if (!custom.detail?.location) {
        return;
      }
      handleSearchRef.current?.({
        location: custom.detail.location,
        radius: custom.detail.radius,
      });
    };

    window.addEventListener('global-location-search', handler);
    return () => window.removeEventListener('global-location-search', handler);
  }, []);

  useEffect(() => {
    const trips = loadSavedTrips();
    setSavedTrips(trips);
  }, []);

  useEffect(() => {
    if (!tripStatusMessage) return;
    const timer = window.setTimeout(() => setTripStatusMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [tripStatusMessage]);

  useEffect(() => {
    if (tripStops.length < 2 && isEditingTripName) {
      setIsEditingTripName(false);
    }
  }, [tripStops.length, isEditingTripName]);

  const focusMapOnStopsRef = useRef<(stops: TripStop[]) => boolean>(() => false);
  const geocodeAndZoomToLocationRef = useRef<(location: string) => Promise<void>>(async () => {});

  const lookupCoordinates = useCallback(
    async (place: string): Promise<GeocodeCoordinate | null> => {
      const normalized = place.trim().toLowerCase();
      if (!normalized) {
        return null;
      }

      if (geocodeCacheRef.current.has(normalized)) {
        return geocodeCacheRef.current.get(normalized)!;
      }

      try {
        const coords = await fetchCoordinatesForPlace(place);
        if (coords) {
          geocodeCacheRef.current.set(normalized, coords);
          return coords;
        }
      } catch (error) {
        console.warn('[Geocode] Lookup failed for', place, error);
      }

      return null;
    },
    []
  );

  const estimateRouteDistance = useCallback(
    async (startLocation: string, endLocation: string): Promise<number | null> => {
      if (!startLocation || !endLocation) {
        return null;
      }
      const [startCoords, endCoords] = await Promise.all([
        lookupCoordinates(startLocation),
        lookupCoordinates(endLocation),
      ]);
      if (!startCoords || !endCoords) {
        return null;
      }
      return haversineDistanceKm(startCoords, endCoords);
    },
    [lookupCoordinates]
  );

  focusMapOnStopsRef.current = (stops: TripStop[]): boolean => {
    if (!mapInstanceRef.current || stops.length === 0) return false;
    if (typeof window === 'undefined' || !window.google) return false;

    const bounds = new google.maps.LatLngBounds();
    let hasCoords = false;

    stops.forEach((stop) => {
      if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
        bounds.extend({ lat: stop.lat, lng: stop.lng });
        hasCoords = true;
      }
    });

    if (!hasCoords) return false;

    mapInstanceRef.current.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });

    const newCenter = bounds.getCenter();
    const fittedZoom = mapInstanceRef.current.getZoom() ?? mapZoom;
    const zoomCap = stops.length <= 2 ? Math.min(fittedZoom, 8) : Math.min(fittedZoom, 11);

    if (zoomCap !== fittedZoom) {
      mapInstanceRef.current.setZoom(zoomCap);
    }

    setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
    setMapZoom(mapInstanceRef.current.getZoom() ?? zoomCap);

    return true;
  };

  const focusMapOnStops = useCallback(
    (stops: TripStop[]): boolean => {
      return focusMapOnStopsRef.current(stops);
    },
    []
  );

  const geocodeAndZoomToLocation = useCallback(
    async (location: string) => geocodeAndZoomToLocationRef.current(location),
    []
  );

  useEffect(() => {
    if (tripStops.length >= 2) {
      setTripRouteKey(computeTripRouteKey(tripStops));
    } else {
      setTripRouteKey(null);
    }
  }, [tripStops]);

  const toggleFeature = (feature: string) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const clearFilters = () => {
    setSelectedFeatures([]);
    setSelectedType('All');
    setRadius(50);
  };

  const handleRouteSearch = async (
    start: string,
    end: string,
    name: string = '',
    waypoints: string[] = [],
    presetStops?: TripStop[],
    options: { preserveActiveTrip?: boolean } = {}
  ) => {
    setIsSearching(true);
    setIsRouteSearch(true);
    setStartPoint(start);
    setEndPoint(end);
    const readableName =
      name ||
      `${start} to ${end}${waypoints.length ? ` via ${waypoints.join(', ')}` : ''}`;
    const trimmedRouteName = readableName.trim();
    setRouteName(trimmedRouteName);
    if (trimmedRouteName) {
      setSearchQuery(trimmedRouteName);
      setOptions([{ value: trimmedRouteName }]);
    }
    // Default to Search mode so users immediately see campsites along the corridor.
    setMapViewMode('search');

    const initialStops =
      presetStops && presetStops.length >= 2
        ? presetStops
        : [
            createTripStopFromName(start, 'start', { locked: true }),
            ...waypoints.map((wp) =>
              createTripStopFromName(wp, 'waypoint', { locked: false })
            ),
            createTripStopFromName(end, 'end', { locked: true }),
          ];

    setTripStops(initialStops);
    setTripRouteKey(computeTripRouteKey(initialStops));
    if (!options.preserveActiveTrip) {
      setActiveTripId(null);
    }

    try {
      const baseCorridorWidth = Math.max(radius, 15);
      let corridorWidthKm = baseCorridorWidth;
      let estimatedDistanceKm: number | null = null;

      try {
        estimatedDistanceKm = await estimateRouteDistance(start, end);
        if (estimatedDistanceKm) {
          const dynamicWidth = Math.min(
            30,
            Math.max(15, Math.round(estimatedDistanceKm / 60))
          );
          corridorWidthKm = dynamicWidth;
        }
      } catch (distanceError) {
        console.warn('[Route search] Unable to estimate distance:', distanceError);
      }

      setRouteCorridorWidth(corridorWidthKm);

      const perPage = estimatedDistanceKm
        ? Math.min(150, Math.max(60, Math.round(estimatedDistanceKm / 7)))
        : 60;

      const params: EnhancedSearchParams = {
        search_type: 'route',
        startPoint: start,
        endPoint: end,
        radius: corridorWidthKm,
        max_deviation: corridorWidthKm,
        corridor_width: corridorWidthKm,
        page: 1,
        per_page: perPage,
        viaPoints: waypoints,
      };

      if (selectedFeatures.length > 0) {
        params.features = selectedFeatures;
      }

      const response = await search(params);
      let searchResults = response.entities || [];
      const seenEntityIds = new Set(searchResults.map((entity) => entity.id));

      if (
        response.total > searchResults.length &&
        response.pages &&
        response.pages > 1
      ) {
        const estimatedDistanceKm = await estimateRouteDistance(start, end);
        const maxAllowedPages = Math.min(
          response.pages,
          Math.max(2, Math.min(5, Math.ceil((estimatedDistanceKm ?? 0) / 250) + 1))
        );

        for (let page = 2; page <= maxAllowedPages; page++) {
          const pagedResult = await search({ ...params, page });
          const pagedEntities = pagedResult.entities || [];

          pagedEntities.forEach((entity) => {
            if (entity?.id && !seenEntityIds.has(entity.id)) {
              seenEntityIds.add(entity.id);
              searchResults.push(entity);
            }
          });

          if (pagedResult.total <= searchResults.length) {
            break;
          }
        }
      }
      
      setEntities(searchResults);
      
      if (sidebarRef.current) {
        sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }

      message.success(`Found ${response.total} campsites along route`);
    } catch (error) {
      console.error('Route search error:', error);
      message.error('Failed to search along route. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRouteSearchRef = useRef<typeof handleRouteSearch>();
  handleRouteSearchRef.current = handleRouteSearch;

  geocodeAndZoomToLocationRef.current = async (location: string) => {
    if (!location || location.length < 3) return;
    
    try {
      const coords = await lookupCoordinates(location);
      if (!coords) {
        return;
      }

      const { lat, lng, boundingBox, type: placeType, class: placeClass } = coords;
      let zoom = 12;
      
      if (boundingBox) {
        const [minLat, maxLat, minLon, maxLon] = boundingBox;
        const latDiff = Math.abs(maxLat - minLat);
        const lonDiff = Math.abs(maxLon - minLon);
        const avgDiff = (latDiff + lonDiff) / 2;
        
        if (avgDiff > 10) zoom = 5;
        else if (avgDiff > 3) zoom = 7;
        else if (avgDiff > 1) zoom = 9;
        else if (avgDiff > 0.3) zoom = 11;
        else if (avgDiff > 0.05) zoom = 13;
        else zoom = 14;
      } else if (placeType === 'country' || placeClass === 'boundary') {
        zoom = 5;
      } else if (placeType === 'state' || placeType === 'administrative') {
        zoom = 7;
      } else if (placeType === 'city' || placeType === 'town') {
        zoom = 11;
      } else if (placeType === 'suburb' || placeType === 'locality') {
        zoom = 13;
      }

      setMapCenter({ lat, lng });
      setMapZoom(zoom);
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };
  
  useEffect(() => {
    if (!mapReady) return;
    if (!isRouteSearch || tripStops.length < 2) return;
    if (mapViewMode !== 'trip') return;
    const focused = focusMapOnStops(tripStops);
    if (!focused && startPoint) {
      geocodeAndZoomToLocation(startPoint);
    }
  }, [mapReady, isRouteSearch, tripStops, mapViewMode, focusMapOnStops, startPoint, geocodeAndZoomToLocation]);
  
  const handleLocationSearch = useCallback(async (value: string) => {

    // Check for route search format
    if (value.includes(' to ')) {
      const [from, to] = value.split(' to ').map(s => s.trim());
      if (from && to) {
        setOptions([{ value: value }]);
      }
      return;
    }

    // Fetch autocomplete suggestions
    if (value.length > 2) {
      try {        
        const suggestions = await getEnhancedLocationSuggestions(value);
        setOptions(suggestions.map((item) => ({ value: item.value })));
        
        if (geocodeTimeoutRef.current) {
          clearTimeout(geocodeTimeoutRef.current);
        }

        if (value.length >= 5) {
          geocodeTimeoutRef.current = setTimeout(() => {
            geocodeAndZoomToLocation(value);
          }, 1500);
        }
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  }, [geocodeAndZoomToLocation]);

  const handleLocationSelect = useCallback((value: string) => {
    setSearchQuery(value);
    
    // Cancel any pending auto-geocode
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
    
    // Only geocode if not a route search
    if (!value.includes(' to ')) {
      geocodeAndZoomToLocation(value);
    }
  }, [geocodeAndZoomToLocation]);

  const getLocationInputValue = useCallback((input: unknown): string => {
    if (typeof input === 'string') {
      return input;
    }
    if (input && typeof input === 'object') {
      const candidate = input as { value?: unknown; label?: unknown };
      if (typeof candidate.value === 'string') {
        return candidate.value;
      }
      if (typeof candidate.label === 'string') {
        return candidate.label;
      }
      if (Array.isArray(input)) {
        return input.filter(Boolean).join(' ');
      }
    }
    if (typeof input === 'number' || typeof input === 'boolean') {
      return String(input);
    }
    return '';
  }, []);

  const handleSearch = async (
    override?: { location?: string; radius?: number }
  ) => {
    const rawLocationInput = override?.location ?? searchQuery;
    const targetLocation = getLocationInputValue(rawLocationInput).trim();
    const targetRadius = override?.radius ?? radius;

    if (!targetLocation) {
      // If a type tab is selected (e.g., Free Camping), do a nationwide bounds search
      if (selectedType !== 'All') {
        setIsRouteSearch(false);
        setTripStops([]);
        setActiveTripId(null);
        setMapViewMode('search');
        setIsSearching(true);

        try {
          // Use bounds-search covering all of Australia instead of geocoding
          const { searchEntitiesByBounds } = await import('@/app/lib/searchEntities');
          const response = await searchEntitiesByBounds({
            bounds: { north: -10, south: -45, east: 155, west: 112 }, // All of Australia
            zoom: 5,
            limit: 200,
            filters: { campsite_type: selectedType },
          });
          const searchResults = response.entities || [];
          setEntities(searchResults);
          if (searchResults.length === 0) {
            message.info(`No ${selectedType.toLowerCase()} found`);
          } else {
            message.success(`Found ${response.total} ${selectedType.toLowerCase()} spots across Australia`);
          }
        } catch (error) {
          console.error('Type search error:', error);
          message.error('Failed to search');
        } finally {
          setIsSearching(false);
        }
        return;
      }
      message.warning('Please enter a location to search');
      return;
    }

    if (override?.location) {
      setSearchQuery(override.location);
    }
    if (override?.radius) {
      setRadius(override.radius);
    }

    const parsedRoute = parseRouteQuery(targetLocation);
    if (parsedRoute) {
      await handleRouteSearch(
        parsedRoute.start,
        parsedRoute.end,
        '',
        parsedRoute.waypoints
      );
      return;
    }

    const hasActiveTrip = tripStops.length > 0;

    setIsRouteSearch(false);
    if (!hasActiveTrip) {
      setTripStops([]);
      setActiveTripId(null);
      setMapViewMode('search');
    }
    setIsSearching(true);

    try {
      const params: EnhancedSearchParams = {
        location: targetLocation,
        radius: targetRadius,
        page: 1,
        per_page: 50,
        search_type: 'location'
      };

      if (selectedType !== 'All') {
        params.type = selectedType;
      }

      if (selectedFeatures.length > 0) {
        params.features = selectedFeatures;
      }

      const response = await search(params);
      
      if (response.entities?.length === 0) {
        message.info(`No campsites found near ${targetLocation}`);
      }
      
      const searchResults = response.entities || [];
      setEntities(searchResults);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (searchResults.length > 0) {
        const coords: { lat: number; lng: number }[] = [];

        searchResults.forEach(entity => {
          if (entity.addresses && entity.addresses[0]) {
            const latStr = entity.addresses[0].gps_latitude;
            const lngStr = entity.addresses[0].gps_longitude;

            if (latStr && lngStr) {
              const lat = parseFloat(latStr);
              const lng = parseFloat(lngStr);

              if (!isNaN(lat) && !isNaN(lng)) {
                coords.push({ lat, lng });
              }
            }
          }
        });

        if (coords.length > 0) {
          // Always update map state so it works in both list and map views
          const lats = coords.map(c => c.lat);
          const lngs = coords.map(c => c.lng);
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          const latSpan = Math.max(...lats) - Math.min(...lats);
          const lngSpan = Math.max(...lngs) - Math.min(...lngs);
          const span = Math.max(latSpan, lngSpan);
          let zoom = 13;
          if (span > 10) zoom = 5;
          else if (span > 5) zoom = 7;
          else if (span > 2) zoom = 8;
          else if (span > 1) zoom = 9;
          else if (span > 0.5) zoom = 10;
          else if (span > 0.2) zoom = 11;
          else if (span > 0.05) zoom = 12;

          setMapCenter({ lat: centerLat, lng: centerLng });
          setMapZoom(zoom);

          // If map instance is available, also use fitBounds for smooth animation
          if (mapInstanceRef.current) {
            const bounds = new google.maps.LatLngBounds();
            coords.forEach(c => bounds.extend(c));
            mapInstanceRef.current.fitBounds(bounds, {
              top: 80, right: 80, bottom: 80, left: 80
            });

            setTimeout(() => {
              const currentZoom = mapInstanceRef.current?.getZoom();
              if (currentZoom && currentZoom > 13) {
                mapInstanceRef.current?.setZoom(13);
              }
            }, 300);
          }
        } else {
          await geocodeAndZoomToLocation(targetLocation);
        }
      } else {
        await geocodeAndZoomToLocation(targetLocation);
      }
      
      if (sidebarRef.current) {
        sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }

      message.success(`Found ${response.total} campsites`);
    } catch (error) {
      console.error('Search error:', error);
      message.error('Failed to search campsites. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };
  const handleSearchRef = useRef<typeof handleSearch>();
  handleSearchRef.current = handleSearch;

  useEffect(() => {
    if (typeof window === 'undefined' || !searchParams) {
      return;
    }
    const startPointParam = searchParams.get('startPoint');
    const endPointParam = searchParams.get('endPoint');
    const routeNameParam = searchParams.get('routeName');
    const isRoute = searchParams.get('isRouteSearch') === 'true';
    const rerunLocation = searchParams.get('rerun');

    if (isRoute && startPointParam && endPointParam) {
      const viaParam = searchParams.get('via');
      const viaList = viaParam
        ? viaParam
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : [];

      const queryString = viaList.length
        ? `${startPointParam} to ${endPointParam} via ${viaList.join(', ')}`
        : `${startPointParam} to ${endPointParam}`;

      setSearchQuery(queryString);

      setTimeout(() => {
        handleRouteSearchRef.current?.(
          startPointParam,
          endPointParam,
          routeNameParam || `${startPointParam} to ${endPointParam}`,
          viaList
        );
      }, 300);
      return;
    }

    const location = rerunLocation || searchParams.get('location');
    if (location) {
      const locationRadius = searchParams.get('radius');
      const parsedRadius = locationRadius ? parseInt(locationRadius, 10) : undefined;

      handleSearchRef.current?.({
        location,
        radius: parsedRadius && !Number.isNaN(parsedRadius) ? parsedRadius : undefined,
      });
    }
  }, [searchParamsKey]);

  // Auto-search after user stops typing
  useEffect(() => {
    if (
      !isRouteSearch &&
      searchQuery &&
      searchQuery.length >= 3 &&
      !searchQuery.includes(' to ')
    ) {
      const timer = setTimeout(() => {
        handleSearch();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [searchQuery, isRouteSearch]);

  const handleMapChange = (bounds: google.maps.LatLngBounds | null) => {
    // Optional: Add bounds change handling
  };

  const focusEntityInList = useCallback((entity: Entity) => {
    setSelectedEntity(entity);
    const entityElement = entityRefs.current.get(entity.id);
    if (entityElement && sidebarRef.current) {
      requestAnimationFrame(() => {
        entityElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }, []);

  const handleRouteDrawn = useCallback((result: google.maps.DirectionsResult) => {
    if (!result?.routes?.length) return;

    const [primaryRoute] = result.routes;
    const legs = primaryRoute.legs ?? [];
    if (!legs.length) return;

    const summary = legs.reduce(
      (acc, leg) => {
        acc.distanceMeters += leg.distance?.value ?? 0;
        acc.durationSeconds += leg.duration?.value ?? 0;
        return acc;
      },
      { distanceMeters: 0, durationSeconds: 0 }
    );

    const distanceKm =
      summary.distanceMeters > 0
        ? Number((summary.distanceMeters / 1000).toFixed(1))
        : undefined;
    const driveMinutes =
      summary.durationSeconds > 0
        ? Math.round(summary.durationSeconds / 60)
        : undefined;

    setRouteSummary((prev) => {
      if (prev.distanceKm === distanceKm && prev.driveMinutes === driveMinutes) {
        return prev;
      }
      return { distanceKm, driveMinutes };
    });

    // Sync stop coordinates with the routed path so markers reflect every stop.
    setTripStops((prev) => {
      if (!prev.length) return prev;
      const updated = prev.map((stop) => ({ ...stop }));

      legs.forEach((leg, index) => {
        const startLocation = leg.start_location;
        const endLocation = leg.end_location;
        if (startLocation && updated[index]) {
          updated[index] = {
            ...updated[index],
            lat: startLocation.lat(),
            lng: startLocation.lng(),
          };
        }
        const endIndex = index + 1;
        if (endLocation && updated[endIndex]) {
          updated[endIndex] = {
            ...updated[endIndex],
            lat: endLocation.lat(),
            lng: endLocation.lng(),
          };
        }
      });

      return updated;
    });
  }, []);

  const handleEntityClick = (entity: Entity) => {
    focusEntityInList(entity);

    const safeName = entity.name || 'campsite';
    const slug = safeName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
    
    const currentParams = new URLSearchParams(window.location.search);
    
    if (searchQuery) {
      currentParams.set('from', searchQuery);
    }
    if (isRouteSearch && startPoint && endPoint) {
      currentParams.set('route', `${startPoint}|${endPoint}`);
    }
    
    const paramsString = currentParams.toString();
    const url = `/apps/campsite-explorer/camp-sites/${encodeURIComponent(slug)}${paramsString ? `?${paramsString}` : ''}`;
    
    router.push(url);
  };

  const handleFreeCampingClick = async () => {
    setSearchQuery('Australia');
    setSelectedType('Free Camping');
    setSelectedFeatures(['Free Overnight Camp']);
    setIsRouteSearch(false);
    setTripStops([]);
    
    setIsSearching(true);
    try {
      const response = await search({
        location: 'Australia',
        radius: 500,
        features: ['Free Overnight Camp'],
        type: 'Free Camping',
        page: 1,
        per_page: 50,
        search_type: 'location'
      });
      
      setEntities(response.entities || []);
      
      if (sidebarRef.current) {
        sidebarRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      message.success(`Found ${response.total} free camping spots`);
    } catch (error) {
      console.error('Free camping search error:', error);
      message.error('Failed to search free camping spots');
    } finally {
      setIsSearching(false);
    }
  };

  // Build bounds search filters from selected type/features for map component
  const boundsSearchFilters = useMemo(() => {
    const filters: { campsite_type?: string; [key: string]: any } = {};
    if (selectedType !== 'All') {
      filters.campsite_type = selectedType;
    }
    return filters;
  }, [selectedType]);

  const tripLegs = useMemo(() => buildTripLegs(tripStops), [tripStops]);
  const tripEntitiesFallback = useMemo(
    () => convertTripStopsToEntities(tripStops),
    [tripStops]
  );
  const tripEntities = useMemo(() => {
    if (!tripStops.length) {
      return [];
    }
    const searchLookup = new Map(entities.map((entity) => [entity.id, entity]));
    return tripStops
      .map((stop, index) => {
        if (stop.entityId && searchLookup.has(stop.entityId)) {
          return searchLookup.get(stop.entityId)!;
        }
        return tripEntitiesFallback[index];
      })
      .filter((entity): entity is Entity => Boolean(entity));
  }, [tripStops, entities, tripEntitiesFallback]);
  const tripStopsWithCoords = useMemo(
    () =>
      tripStops.filter(
        (stop) => typeof stop.lat === 'number' && typeof stop.lng === 'number'
      ),
    [tripStops]
  );
  const tripEntityIds = useMemo(() => {
    const ids = tripStops
      .map((stop) => stop.entityId)
      .filter((id): id is number => typeof id === 'number');
    return new Set(ids);
  }, [tripStops]);

  useEffect(() => {
    const prevCount = prevTripStopCountRef.current;
    const hasStops = tripStops.length > 0;

    if (isRouteSearch && tripStops.length >= 2) {
      setIsTripSectionOpen(true);
    } else if (!isRouteSearch && hasStops && prevCount === 0) {
      setIsTripSectionOpen(true);
    } else if (!isRouteSearch && !hasStops && prevCount > 0) {
      setIsTripSectionOpen(false);
    }

    prevTripStopCountRef.current = tripStops.length;
  }, [isRouteSearch, tripStops.length]);

  const isTripView = mapViewMode === 'trip';

  useEffect(() => {
    if (tripStops.length === 0 && mapViewMode === 'trip') {
      setMapViewMode('search');
    }
  }, [tripStops.length, mapViewMode, setMapViewMode]);

  const searchPanelTitle =
    mapViewMode === 'trip'
      ? tripStops.length >= 2
        ? 'Trip stops'
        : 'Trip ladder'
      : isRouteSearch
        ? 'Campsites along your trip'
        : searchQuery
          ? `Results for ${searchQuery}`
          : 'Featured campsites';
  const listIsLoading = mapViewMode !== 'trip' && isSearching;
  const tripPanelTitle =
    tripStops.length >= 2 ? 'Your trip itinerary' : 'Trip ladder';

  const searchResultCount =
    mapViewMode === 'trip' ? tripStops.length : isSearching ? 0 : entities.length;
  const hasTripStops = tripStops.length > 0;
  const hasBuiltTrip = isRouteSearch && tripStops.length >= 2;

  const mapEntities = useMemo(() => {
    if (!isTripView) return entities;
    return tripEntities;
  }, [isTripView, entities, tripEntities]);
  const visibleEntities = useMemo(
    () => (mapViewMode === 'trip' ? tripEntities : entities),
    [mapViewMode, tripEntities, entities]
  );
  useEffect(() => {
    if (mapViewMode === 'trip') {
      setVisibleCardCount(visibleEntities.length);
    } else {
      setVisibleCardCount(Math.min(48, visibleEntities.length));
    }
  }, [visibleEntities.length, mapViewMode]);

  const waypointNamesForRoute = useMemo(
    () =>
      tripStops
        .slice(1, -1)
        .map((stop) => stop.name)
        .filter(Boolean),
    [tripStops]
  );

  const ladderRouteConfig = useMemo(() => {
    if (tripStopsWithCoords.length < 2) {
      return null;
    }

    const toWaypointString = (stop: TripStop) => {
      if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
        return `${stop.lat},${stop.lng}`;
      }
      return stop.name;
    };

    const startStop = tripStopsWithCoords[0];
    const endStop = tripStopsWithCoords[tripStopsWithCoords.length - 1];

    return {
      start: toWaypointString(startStop),
      end: toWaypointString(endStop),
      color: '#3B5998',
      strokeWeight: 4,
      maxDeviationKm: routeCorridorWidth,
      waypoints: tripStopsWithCoords.slice(1, -1).map(toWaypointString),
      routeKey: tripRouteKey || computeTripRouteKey(tripStopsWithCoords),
    };
  }, [tripStopsWithCoords, routeCorridorWidth, tripRouteKey]);

  const activeRouteConfig = useMemo(() => {
    if (isRouteSearch && startPoint && endPoint) {
      return {
        start: startPoint,
        end: endPoint,
        color: '#3B5998',
        strokeWeight: 4,
        maxDeviationKm: routeCorridorWidth,
        waypoints: waypointNamesForRoute,
        routeKey: tripRouteKey || null,
      };
    }
    return ladderRouteConfig;
  }, [
    isRouteSearch,
    startPoint,
    endPoint,
    routeCorridorWidth,
    waypointNamesForRoute,
    tripRouteKey,
    ladderRouteConfig,
  ]);

  const shouldShowRouteOnMap =
    !!activeRouteConfig &&
    tripStops.length >= 2 &&
    (isRouteSearch || mapViewMode === 'trip');

  const activeMapCount = mapEntities.length;
  const mapCountLabel =
    mapViewMode === 'trip'
      ? 'trip stops'
      : hasBuiltTrip
        ? 'trip stops on route'
        : 'campsites';

  const handleAddEntityToTrip = useCallback(
    (entity: Entity) => {
      const stop = createTripStopFromEntity(entity);
      let didAdd = false;
      let previousCount = 0;
      let nextStopsSnapshot: TripStop[] = [];

      setTripStops((prev) => {
        previousCount = prev.length;
        const result = mergeTripStopIntoList(prev, stop);
        if (!result.added) {
          return prev;
        }

        didAdd = true;
        nextStopsSnapshot = result.next;
        return result.next;
      });

      if (!didAdd) {
        message.info('That campsite is already in your trip.');
        return;
      }

      setActiveTripId(null);
      setRouteName((currentName) => {
        const firstStopName = nextStopsSnapshot[0]?.name;
        const defaultFirstName =
          firstStopName && firstStopName.length > 0 ? `${firstStopName} trip` : 'My Trip';

        if (previousCount === 0) {
          return defaultFirstName;
        }

        if (previousCount === 1 && nextStopsSnapshot.length >= 2) {
          const firstName = nextStopsSnapshot[0]?.name ?? entity.name ?? 'Start';
          const lastName =
            nextStopsSnapshot[nextStopsSnapshot.length - 1]?.name ?? entity.name ?? 'Finish';
          const proposedName = `${firstName} to ${lastName}`;

          if (!currentName || currentName === defaultFirstName) {
            return proposedName;
          }
        }

        return currentName;
      });

      setMapViewMode('trip');

      if (previousCount === 0) {
        message.success('Trip created');
      } else {
        message.success('Added to trip');
      }
    },
    [setActiveTripId, setRouteName, setMapViewMode]
  );

  const handleStartTripSave = useCallback(() => {
    if (tripStops.length < 2) {
      setTripStatusMessage({
        message: 'Add at least two stops before saving.',
        tone: 'warning',
      });
      return;
    }

    setTripNameInput(
      routeName ||
        (tripStops.length >= 2
          ? `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`
          : 'New Trip')
    );
    setTripStatusMessage(null);
    setIsEditingTripName(true);
  }, [routeName, tripStops]);

  const handleSaveTripConfirm = useCallback(() => {
    if (tripStops.length < 2) {
      setTripStatusMessage({
        message: 'Add at least two stops before saving.',
        tone: 'warning',
      });
      return;
    }

    const trimmedName = tripNameInput.trim();
    if (!trimmedName) {
      setTripStatusMessage({
        message: 'Please give your trip a name.',
        tone: 'warning',
      });
      return;
    }

    try {
      const id = tripRouteKey || activeTripId || uniqueId();
      const storedTrip: StoredTrip = {
        id,
        name: trimmedName,
        createdAt: new Date().toISOString(),
        routeName: routeName || trimmedName,
        startPoint,
        endPoint,
        routeKey: tripRouteKey || undefined,
        stops: tripStops,
      };

      const updatedTrips = upsertTrip(storedTrip);
      setSavedTrips(updatedTrips);
      setActiveTripId(id);
      setTripNameInput(trimmedName);
      setTripStatusMessage({
        message: `Saved "${trimmedName}"`,
        tone: 'success',
      });
      setIsEditingTripName(false);
    } catch (error) {
      console.error('Error saving trip:', error);
      setTripStatusMessage({
        message: 'Unable to save trip. Please try again.',
        tone: 'error',
      });
    }
  }, [tripStops, tripNameInput, tripRouteKey, activeTripId, routeName, startPoint, endPoint]);

  const handleCancelTripSave = useCallback(() => {
    setIsEditingTripName(false);
    setTripStatusMessage(null);
  }, []);

  const handleLoadTrip = useCallback(
    async (tripId: string) => {
      if (!tripId) return;
      const trip = savedTrips.find((item) => item.id === tripId);
      if (!trip) return;
      setActiveTripId(tripId);
      const waypoints = trip.stops
        .slice(1, -1)
        .map((stop) => stop.name)
        .filter(Boolean);
      await handleRouteSearch(
        trip.startPoint || trip.stops[0]?.name || '',
        trip.endPoint || trip.stops[trip.stops.length - 1]?.name || '',
        trip.routeName || trip.name,
        waypoints,
        trip.stops,
        { preserveActiveTrip: true }
      );
    },
    [savedTrips, handleRouteSearch]
  );

  const handleTripReorder = useCallback((newStops: TripStop[]) => {
    setTripStops(newStops);
  }, []);

  const handleTripRemove = useCallback((stopId: string) => {
    setTripStops((prev) => prev.filter((stop) => stop.id !== stopId));
  }, []);

  const handleAssistantSend = useCallback(
    async (rawMessage: string) => {
      const trimmed = rawMessage.trim();
      if (!trimmed) return;

      const userMessage: AssistantMessage = {
        id: uniqueId(),
        role: 'user',
        content: trimmed,
        createdAt: Date.now(),
      };

      setAssistantMessages((prev) => [...prev, userMessage]);
      setAssistantIsProcessing(true);

      const responseParts: string[] = [];
      const lower = trimmed.toLowerCase();
      let nextRadius = radius;
      let nextCorridor = routeCorridorWidth;

      const extractNumber = (pattern: RegExp): number | null => {
        const match = trimmed.match(pattern);
        if (!match) return null;
        const value = Number.parseInt(match[1], 10);
        return Number.isFinite(value) ? value : null;
      };

      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), max);

      try {
        const radiusValue = extractNumber(/(?:radius|search area|range)\D*(\d{1,3})/i);
        if (radiusValue !== null) {
          const bounded = clamp(radiusValue, 5, 250);
          setRadius(bounded);
          nextRadius = bounded;
          responseParts.push(`Okay, I set the location search radius to ${bounded} km.`);
        }

        const corridorValue = extractNumber(/(?:corridor|buffer|deviation)\D*(\d{1,3})/i);
        if (corridorValue !== null) {
          const bounded = clamp(corridorValue, 10, 400);
          setRouteCorridorWidth(bounded);
          nextCorridor = bounded;
          responseParts.push(`I widened the corridor to ${bounded} km.`);
        }

        const removeMatch = trimmed.match(
          /\b(?:remove|delete|drop)\b\s+(?:the\s+)?(?:stop|campsite|camp|place|location)?\s*(.+)/i
        );
        if (removeMatch) {
          let candidate = removeMatch[1].trim();
          candidate = candidate.replace(/\b(?:from|to|into|please|thanks|thank you)\b.*$/i, '').trim();
          const candidateLower = candidate.toLowerCase();
          const target = tripStops.find((stop) =>
            stop.name.toLowerCase().includes(candidateLower)
          );
          if (!target) {
            responseParts.push(`I couldn't find a stop matching "${candidate}".`);
          } else if (target.locked) {
            responseParts.push(`I can't remove ${target.name} because it anchors the trip.`);
          } else {
            setTripStops((prev) => prev.filter((stop) => stop.id !== target.id));
            const remaining = Math.max(tripStops.length - 1, 0);
            responseParts.push(
              `Removed ${target.name} from the itinerary. You now have ${remaining} stop${remaining === 1 ? '' : 's'}.`
            );
          }
        }

        let addCandidate: string | null = null;
        const addStopMatch = trimmed.match(
          /\badd\s+(?:another\s+)?(?:stop|campsite|camp|waypoint)\s+(.+)/i
        );
        if (addStopMatch) {
          addCandidate = addStopMatch[1];
        } else {
          const addTripMatch = trimmed.match(/\badd\s+(.+?)\s+(?:to|into)\s+(?:the\s+)?(?:trip|route)\b/i);
          if (addTripMatch) {
            addCandidate = addTripMatch[1];
          }
        }

        if (addCandidate) {
          let cleaned = addCandidate
            .replace(/\b(?:please|thanks|thank you)\b.*$/i, '')
            .replace(/\b(?:as|for)\b.*$/i, '')
            .trim();

          if (cleaned.length > 2) {
            responseParts.push(`Let me look for "${cleaned}"...`);
            try {
              const result = await search({
                location: cleaned,
                radius: Math.max(radius, 50),
                per_page: 5,
                search_type: 'location',
              } as EnhancedSearchParams);

              const entity = result.entities?.[0];
              if (!entity) {
                responseParts.push(`I couldn't find a campsite for "${cleaned}". Try a different name or nearby town.`);
              } else {
                let addition: { added: boolean; next: TripStop[] } | undefined;
                const stop = createTripStopFromEntity(entity);
                setTripStops((prev) => {
                  const outcome = mergeTripStopIntoList(prev, stop);
                  addition = outcome;
                  return outcome.added ? outcome.next : prev;
                });
                if (addition?.added) {
                  const count = addition.next.length;
                  responseParts.push(
                    `Added ${entity.name} just before your final stop. You now have ${count} stop${count === 1 ? '' : 's'}.`
                  );
                  if (mapViewMode !== 'trip') {
                    setMapViewMode('trip');
                  }
                } else if (addition) {
                  responseParts.push(`${entity.name} is already on your ladder.`);
                } else {
                  responseParts.push('Something went wrong while adding that stop. Please try again.');
                }
              }
            } catch (error) {
              console.error('Assistant add stop failed', error);
              responseParts.push('Something went wrong while searching for that stop. Please try again in a moment.');
            }
          }
        }

        if (/what.*stops|current.*plan|show.*(trip|stops)|summary/i.test(lower)) {
          if (tripStops.length === 0) {
            responseParts.push('The trip ladder is empty right now.');
          } else {
            const summary = tripStops
              .map((stop, index) => `${index + 1}. ${stop.name}${stop.locked ? ' (locked)' : ''}`)
              .join('\n');
            responseParts.push(`Here is your current ladder:\n${summary}`);
          }
        }

        if (!responseParts.length) {
          responseParts.push(
            'I can: add or remove stops, adjust the radius, tweak the corridor width, or recap your itinerary. Try "Add a campsite near Bright" or "Set the radius to 120 km".'
          );
        }
      } catch (error) {
        console.error('Trip assistant error', error);
        responseParts.push('I hit a snag processing that request. Please try again.');
      }

      const fallbackText =
        responseParts.length > 0
          ? responseParts.join('\n\n')
          : 'Let me know how you want to adjust your trip.';

      let handledByAgent = false;

      if (agentSessionId) {
        try {
          const payload = await sendAgentMessage({
            sessionId: agentSessionId,
            content: trimmed,
            taskType: inferAssistantTaskType(trimmed),
            agent: agentHandoff || undefined,
            contextUpdates: {
              itinerary_summary: summariseTripForAssistant(tripStops),
              action_summary: fallbackText,
              radius_km: String(nextRadius),
              corridor_width_km: String(nextCorridor),
              last_search_query: searchQuery || '',
              last_view_mode: mapViewMode,
              trip_stop_count: String(tripStops.length),
            },
          });

          if (payload.context) {
            setAgentContext(payload.context);
          }
          setAgentHandoff(payload.handoff || null);

          if (payload.messages?.length) {
            const mapped: AssistantMessage[] = payload.messages.map((msg) => ({
              id: uniqueId(),
              role: (msg.role === 'user' ? 'user' : 'assistant') as AssistantRole,
              content: msg.content ?? '',
              createdAt: Date.now(),
            }));
            setAssistantMessages((prev) => [...prev, ...mapped]);
            if (!assistantOpen) {
              setAssistantHasUnread(true);
            }
          }
          handledByAgent = true;
        } catch (error) {
          console.error('Agent assistant error', error);
          message.error('Trip assistant is unavailable, falling back to a local response.');
        }
      } else {
        message.warning('Trip assistant session is still initialising; using a local response for now.');
      }

      if (!handledByAgent) {
        setAssistantMessages((prev) => [
          ...prev,
          {
            id: uniqueId(),
            role: 'assistant',
            content: fallbackText,
            createdAt: Date.now(),
          },
        ]);
        if (!assistantOpen) {
          setAssistantHasUnread(true);
        }
      }

      setAssistantIsProcessing(false);
    },
    [
      agentHandoff,
      agentSessionId,
      assistantOpen,
      mapViewMode,
      radius,
      routeCorridorWidth,
      searchQuery,
      tripStops,
    ]
  );
  const ladderActions = useMemo(() => {
    if (!hasTripStops) return undefined;

    const toneClassMap = {
      success: 'bg-emerald-50 text-emerald-700',
      warning: 'bg-amber-50 text-amber-700',
      error: 'bg-rose-50 text-rose-700',
    } as const;

    const saveDisabled =
      tripStops.length < 2 || (isEditingTripName && !tripNameInput.trim());

    return (
      <div className="flex w-full flex-col gap-2">
        {tripStatusMessage && (
          <div
            className={`rounded-md px-3 py-1.5 text-[11px] font-medium transition-opacity duration-300 ${toneClassMap[tripStatusMessage.tone]}`}
          >
            {tripStatusMessage.message}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5">
          {savedTrips.length > 0 && (
            <select
              value={activeTripId ?? ''}
              onChange={(event) => handleLoadTrip(event.target.value)}
              className="h-7 rounded-md border border-gray-200 bg-white px-2 text-[11px] font-medium text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
            >
              <option value="">Load trip...</option>
              {savedTrips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.name}
                </option>
              ))}
            </select>
          )}

          {isEditingTripName && (
            <Input
              size="small"
              value={tripNameInput}
              onChange={(event) => setTripNameInput(event.target.value)}
              placeholder="Trip name"
              className="!h-7 w-36 !rounded-md border-gray-200 text-[11px]"
              autoFocus
            />
          )}

          <button
            type="button"
            onClick={isEditingTripName ? handleSaveTripConfirm : handleStartTripSave}
            disabled={saveDisabled}
            className={`h-7 rounded-md px-3 text-[11px] font-semibold transition-all ${
              saveDisabled
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-blue-600 text-white hover:bg-blue-500'
            }`}
          >
            {isEditingTripName ? 'Save' : 'Save Trip'}
          </button>

          {isEditingTripName && (
            <button
              type="button"
              onClick={handleCancelTripSave}
              className="h-7 px-2 text-[11px] font-medium text-gray-500 transition hover:text-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }, [
    hasTripStops,
    savedTrips,
    activeTripId,
    handleLoadTrip,
    handleSaveTripConfirm,
    handleStartTripSave,
    handleCancelTripSave,
    tripStops,
    tripStatusMessage,
    isEditingTripName,
    tripNameInput,
  ]);

  const renderTripPlannerInline = useCallback(
    () => (
      <TripPlannerLadder
        stops={tripStops}
        legs={tripLegs}
        onReorder={handleTripReorder}
        onRemoveStop={handleTripRemove}
        title={tripPanelTitle}
        actions={ladderActions}
        totalsOverride={routeSummary}
        compact
      />
    ),
    [tripStops, tripLegs, handleTripReorder, handleTripRemove, tripPanelTitle, ladderActions, routeSummary]
  );

  const renderTripPlannerAccordion = useCallback(
    (extraClassName: string = '') => {
      if (!hasTripStops) return null;
      return (
        <div className={`rounded-lg border border-gray-200 bg-white shadow-sm ${extraClassName}`}>
          <button
            type="button"
            onClick={() => setIsTripSectionOpen((prev) => !prev)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
            aria-expanded={isTripSectionOpen}
          >
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-blue-600">
                {tripStops.length}
              </div>
              <p className="text-sm font-medium text-gray-900">Trip stops</p>
            </div>
            <DownOutlined
              className={`text-[10px] text-gray-400 transition-transform ${
                isTripSectionOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {isTripSectionOpen && (
            <div className="border-t border-gray-100 p-2">
              {renderTripPlannerInline()}
            </div>
          )}
        </div>
      );
    },
    [
      isTripSectionOpen,
      hasTripStops,
      tripStops.length,
      renderTripPlannerInline,
    ]
  );

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <HeroSearch
        searchQuery={searchQuery}
        selectedType={selectedType}
        showFilters={showFilters}
        selectedFeatures={selectedFeatures}
        radius={radius}
        options={options}
        isSearching={isSearching}
        onLocationSearch={handleLocationSearch}
        onLocationSelect={handleLocationSelect}
        onLocationChange={setSearchQuery}
        onTypeChange={setSelectedType}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onToggleFeature={toggleFeature}
        onClearFilters={clearFilters}
        onRadiusChange={setRadius}
        onSearch={handleSearch}
        onPressEnter={handleSearch}
        features={features}
        assistantMessages={assistantMessages}
        assistantSuggestions={assistantSuggestions}
        assistantIsOpen={assistantOpen}
        assistantHasUnread={assistantHasUnread}
        assistantIsProcessing={assistantBooting || assistantIsProcessing}
        onToggleAssistant={toggleAssistant}
        onSendAssistant={handleAssistantSend}
      />

      {/* Mobile tabs */}
      <div className="lg:hidden flex bg-white shadow-sm sticky top-0 z-20">
        <button
          onClick={() => setViewMode('list')}
          className={`flex-1 py-3.5 text-center text-sm font-semibold transition-all relative ${
            viewMode === 'list' ? 'text-blue-600' : 'text-gray-500'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            List
            {!isSearching && entities.length > 0 && (
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {entities.length}
              </span>
            )}
          </span>
          {viewMode === 'list' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t" />
          )}
        </button>
        <button
          onClick={() => setViewMode('map')}
          className={`flex-1 py-3.5 text-center text-sm font-semibold transition-all relative ${
            viewMode === 'map' ? 'text-blue-600' : 'text-gray-500'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            Map
            {hasTripStops && (
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                viewMode === 'map' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {tripStops.length}
              </span>
            )}
          </span>
          {viewMode === 'map' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t" />
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-hidden min-h-[520px]">
        {/* MOBILE LIST VIEW */}
        <div 
          className={`lg:hidden absolute inset-0 flex flex-col bg-white ${
            viewMode === 'list' ? 'block' : 'hidden'
          }`}
        >
          <div className="flex-shrink-0 px-4 py-3 border-b bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                {isSearching ? 'Searching...' : searchPanelTitle}
              </h2>
              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                {searchResultCount}
              </span>
            </div>
          </div>

          <div ref={sidebarRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {renderTripPlannerAccordion()}

            {isSearching ? (
              <div className="space-y-2.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="animate-pulse rounded-lg border border-gray-200 bg-white overflow-hidden">
                    <div className="aspect-[16/9] sm:aspect-[4/3] bg-gray-200" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                      <div className="flex gap-1.5 pt-1">
                        <div className="h-5 w-5 bg-gray-100 rounded" />
                        <div className="h-5 w-5 bg-gray-100 rounded" />
                        <div className="h-5 w-5 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : entities.length === 0 ? (
              <div className="p-8 text-center bg-white rounded-lg border border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <EnvironmentOutlined style={{ fontSize: '28px', color: '#9ca3af' }} />
                </div>
                <p className="text-gray-800 font-semibold">No campsites found</p>
                <p className="text-gray-500 text-sm mt-1 mb-4">Try a different location or broaden your filters</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Byron Bay', 'Noosa', 'Margaret River', 'Wilsons Promontory'].map((place) => (
                    <button
                      key={place}
                      type="button"
                      onClick={() => {
                        setSearchQuery(place);
                        handleSearchRef.current?.({ location: place });
                      }}
                      className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    >
                      {place}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                {entities.map((entity) => (
                  <div
                    key={entity.id}
                    ref={(el) => {
                      if (el) entityRefs.current.set(entity.id, el);
                      else entityRefs.current.delete(entity.id);
                    }}
                  >
                    <EntityCard
                      entity={entity}
                      isSelected={selectedEntity?.id === entity.id}
                      onClick={() => handleEntityClick(entity)}
                      layout="grid"
                      onAddToTrip={handleAddEntityToTrip}
                      isInTrip={tripStops.some((stop) => stop.entityId === entity.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* MOBILE MAP VIEW */}
        <div
          className={`lg:hidden absolute inset-0 ${
            viewMode === 'map' ? 'block' : 'hidden'
          }`}
        >
          {hasTripStops && (
            <div className="absolute top-4 left-4 z-10 flex items-center gap-1 rounded-full border border-white/60 bg-white/90 px-1 py-1 shadow-lg backdrop-blur-sm">
              <button
                onClick={() => setMapViewMode('search')}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                  mapViewMode === 'search'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-700 hover:bg-white/80'
                }`}
              >
                Search
              </button>
              <button
                onClick={() => setMapViewMode('trip')}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                  mapViewMode === 'trip'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-700 hover:bg-white/80'
                }`}
              >
                Trip
              </button>
            </div>
          )}

          <GoogleMapComponent
            key={`mobile-map-${mapViewMode}`}
            entities={mapEntities}
            center={shouldShowRouteOnMap ? undefined : mapCenter}
            zoom={shouldShowRouteOnMap ? undefined : mapZoom}
            routeConfig={shouldShowRouteOnMap ? activeRouteConfig : null}
            onRouteDrawn={handleRouteDrawn}
            onEntitySelected={(entity) => {
              handleEntityClick(entity);
            }}
            onMapChange={handleMapChange}
            onMapReady={(map) => {
              mapInstanceRef.current = map;
              setMapReady(true);
            }}
            enableClustering={mapViewMode !== 'trip'}
            useBoundsLoading={mapViewMode !== 'trip' && !isRouteSearch}
            boundsSearchFilters={boundsSearchFilters}
            showCampsites={true}
            onAddTripStop={handleAddEntityToTrip}
            tripStopEntityIds={hasTripStops ? tripEntityIds : undefined}
          />

          {/* Counter badge */}
          <div className="absolute bottom-5 left-4 z-10 rounded-full border border-white/60 bg-white/90 px-4 py-2 shadow-lg backdrop-blur-sm">
            <p className="text-sm font-semibold text-gray-900">
              <span className="text-blue-600 text-base font-bold">{activeMapCount}</span>
              <span className="text-gray-600 ml-1">{mapCountLabel}</span>
            </p>
          </div>
        </div>
        {/* DESKTOP: Side-by-side layout */}
        <div className="hidden lg:flex absolute inset-0 mx-auto max-w-[1920px]">
          {/* Map */}
          <div className="flex-1 relative">
          <GoogleMapComponent
            key={`desktop-map-${mapViewMode}`}
            entities={mapEntities}
            center={shouldShowRouteOnMap ? undefined : mapCenter}
            zoom={shouldShowRouteOnMap ? undefined : mapZoom}
            routeConfig={shouldShowRouteOnMap ? activeRouteConfig : null}
            onRouteDrawn={handleRouteDrawn}
            onEntitySelected={(entity) => {
              focusEntityInList(entity);
            }}
            onMapChange={handleMapChange}
            onMapReady={(map) => {
                mapInstanceRef.current = map;
                setMapReady(true);
              }}
              enableClustering={mapViewMode !== 'trip'}
              useBoundsLoading={mapViewMode !== 'trip' && !isRouteSearch}
              boundsSearchFilters={boundsSearchFilters}
              showCampsites={true}
              onAddTripStop={handleAddEntityToTrip}
              tripStopEntityIds={hasTripStops ? tripEntityIds : undefined}
            />

            {hasTripStops && (
              <div className="absolute top-5 right-5 z-10 flex items-center gap-1 rounded-full border border-white/60 bg-white/90 px-1 py-1 shadow-xl backdrop-blur-sm">
                <button
                  onClick={() => setMapViewMode('search')}
                  className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                    mapViewMode === 'search'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 hover:bg-white/80'
                  }`}
                >
                  <SearchOutlined style={{ fontSize: '17px' }} />
                  <span>Search</span>
                </button>
                <button
                  onClick={() => setMapViewMode('trip')}
                  className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                    mapViewMode === 'trip'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 hover:bg-white/80'
                  }`}
                >
                  <AimOutlined style={{ fontSize: '17px' }} />
                  <span>Trip</span>
                </button>
              </div>
            )}

            {/* Counter */}
            <div className="absolute bottom-5 left-5 z-10 rounded-full border border-white/60 bg-white/90 px-5 py-3 shadow-xl backdrop-blur-sm">
              <p className="text-sm font-semibold text-gray-900">
                <span className="text-blue-600 text-lg font-bold">{activeMapCount}</span>
                <span className="text-gray-700 ml-1.5">{mapCountLabel}</span>
                {isRouteSearch && <span className="text-gray-500 text-xs ml-2">on route</span>}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-[440px] bg-white border-l border-gray-200 flex flex-col shadow-xl">
            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900">
                {mapViewMode === 'trip'
                  ? 'Your Trip'
                  : isSearching
                    ? 'Searching...'
                    : searchPanelTitle}
              </h2>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                {searchResultCount}
              </span>
            </div>
          </div>

          <div ref={sidebarRef} className="flex-1 overflow-y-auto p-3 bg-gray-50 space-y-3">
            {mapViewMode === 'trip' ? (
              /* TRIP MODE: show ladder inline at top */
              <div className="space-y-3">
                {renderTripPlannerInline()}

                {/* Trip stop entity cards below the ladder */}
                {visibleEntities.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {visibleEntities.slice(0, visibleCardCount).map((entity) => (
                      <div
                        key={entity.id}
                        ref={(el) => {
                          if (el) entityRefs.current.set(entity.id, el);
                          else entityRefs.current.delete(entity.id);
                        }}
                      >
                        <EntityCard
                          entity={entity}
                          isSelected={selectedEntity?.id === entity.id}
                          onClick={() => handleEntityClick(entity)}
                          layout="grid"
                          isInTrip={tripStops.some((stop) => stop.entityId === entity.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* SEARCH MODE: accordion + search results */
              <>
                {renderTripPlannerAccordion()}
                {listIsLoading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="animate-pulse rounded-lg border border-gray-200 bg-white overflow-hidden">
                        <div className="aspect-[4/3] bg-gray-200" />
                        <div className="p-2.5 space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-3/4" />
                          <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                          <div className="flex gap-1.5 pt-1">
                            <div className="h-5 w-5 bg-gray-100 rounded" />
                            <div className="h-5 w-5 bg-gray-100 rounded" />
                            <div className="h-5 w-5 bg-gray-100 rounded" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : visibleEntities.length === 0 ? (
                  <div className="p-10 text-center bg-white border border-gray-200 rounded-lg">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                      <EnvironmentOutlined style={{ fontSize: '28px', color: '#9ca3af' }} />
                    </div>
                    <p className="text-gray-800 font-semibold">No campsites found</p>
                    <p className="text-gray-500 text-sm mt-1 mb-4">Try a different location or broaden your filters</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {['Byron Bay', 'Noosa', 'Margaret River', 'Wilsons Promontory'].map((place) => (
                        <button
                          key={place}
                          type="button"
                          onClick={() => {
                            setSearchQuery(place);
                            handleSearchRef.current?.({ location: place });
                          }}
                          className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                        >
                          {place}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-3">
                      {visibleEntities.slice(0, visibleCardCount).map((entity) => (
                        <div
                          key={entity.id}
                          ref={(el) => {
                            if (el) entityRefs.current.set(entity.id, el);
                            else entityRefs.current.delete(entity.id);
                          }}
                        >
                          <EntityCard
                            entity={entity}
                            isSelected={selectedEntity?.id === entity.id}
                            onClick={() => handleEntityClick(entity)}
                            layout="grid"
                            onAddToTrip={handleAddEntityToTrip}
                            isInTrip={tripStops.some((stop) => stop.entityId === entity.id)}
                          />
                        </div>
                      ))}
                    </div>

                    {visibleCardCount < visibleEntities.length && (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={() =>
                            setVisibleCardCount((count) =>
                              Math.min(count + 40, visibleEntities.length)
                            )
                          }
                          className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-blue-400 hover:text-blue-600"
                        >
                          Show{' '}
                          {Math.min(visibleEntities.length - visibleCardCount, 40)} more results
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Back to top button */}
          {showBackToTop && (
            <button
              type="button"
              onClick={scrollToTop}
              className="absolute bottom-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-lg transition-all hover:bg-blue-600 hover:text-white hover:border-blue-600 hover:shadow-xl"
              aria-label="Scroll to top"
            >
              <DownOutlined className="rotate-180 text-sm" />
            </button>
          )}
        </div>
      </div>
      </div>

    </div>
  );
}

export default function HomePage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Spin size="large" />
      </div>
    }>
      <HomePageContent />
    </Suspense>
  );
}
